<template>
    <b-modal id="update-asset-limit" title="Edit Asset Capacity" ok-title="Save" cancel-title="Cancel" ref="modal"
        @ok="handleOk" :cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons"
        :no-close-on-backdrop="true" centered>
        <loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

        <span class="mx-0">
            Enter the asset limit configuration for <strong>{{ distribution.connectedCompany }}</strong>
        </span>
        <b-form @submit.stop.prevent="handleSubmit" novalidate>
            <b-row class="mt-4 mx-0">
                <strong> {{ distribution.assetType }}</strong>
            </b-row>
            <b-row class="mt-4 mx-0 align-items-center">
                <b-col sm="5" class="px-0 d-flex flex-column">
                    Contract Based
                    <small class="ml-2 text-muted">Based from Billing Contract</small>
                </b-col>
                <b-col sm="7" class="px-0">
                    <b-input-group>
                        <b-input-group-prepend>
                            <b-button id="less-limit" name="less-limit" variant="outline-secondary"
                                @click="decrementLimit('assetLimit')">&nbsp; - &nbsp;</b-button>
                        </b-input-group-prepend>

                        <b-form-input id="asset-limit" name="Asset Limit" type="number" step="1"
                            v-model.number="distribution.assetLimit"
                            v-validate="{ required: true, min_value: 1, max_value: 1000000 }" placeholder="0"
                            class="text-center" @keydown="blockInvalidCharacters" />

                        <b-input-group-append>
                            <b-button id="add-limit" name="add-limit" variant="outline-secondary"
                                @click="incrementLimit('assetLimit')">&nbsp; + &nbsp;</b-button>
                        </b-input-group-append>
                    </b-input-group>
                    <span v-show="errors.has('Asset Limit')" class="help-block">
                        {{ errors.first('Asset Limit') }}
                    </span>
                </b-col>
            </b-row>
            <b-row class="mt-4 mx-0 align-items-center">
                <b-col sm="5" class="px-0 d-flex flex-column">
                    Max Stock Qty
                    <small class="ml-2 text-muted">Maximum stock per asset</small>
                </b-col>
                <b-col sm="7" class="px-0">
                    <b-input-group>
                        <b-input-group-prepend>
                            <b-button id="less-limit-max" name="less-limit-max" variant="outline-secondary"
                                @click="decrementLimit('maxStockLimit')">&nbsp; - &nbsp;</b-button>
                        </b-input-group-prepend>

                        <b-form-input id="max-stock" name="Max Stock" type="number" step="1"
                            v-model.number="distribution.maxStockLimit"
                            v-validate="{ required: true, min_value: 1, max_value: 1000000 }" placeholder="0"
                            class="text-center" @keydown="blockInvalidCharacters" />

                        <b-input-group-append>
                            <b-button id="add-limit-max" name="add-limit-max" variant="outline-secondary"
                                @click="incrementLimit('maxStockLimit')">&nbsp; + &nbsp;</b-button>
                        </b-input-group-append>
                    </b-input-group>
                    <span v-show="errors.has('Max Stock')" class="help-block">
                        {{ errors.first('Max Stock') }}
                    </span>
                </b-col>
            </b-row>
            <b-row class="mt-4 mx-0 align-items-center">
                <b-col sm="5" class="px-0 d-flex flex-column">
                    Min Stock Qty
                    <small class="ml-2 text-muted">Minimum stock per asset</small>
                </b-col>
                <b-col sm="7" class="px-0">
                    <b-input-group>
                        <b-input-group-prepend>
                            <b-button id="less-limit-min" name="less-limit-min" variant="outline-secondary"
                                @click="decrementLimit('minStockLimit')">&nbsp; - &nbsp;</b-button>
                        </b-input-group-prepend>

                        <b-form-input id="min-stock" name="Min Stock" type="number" step="1"
                            v-model.number="distribution.minStockLimit"
                            v-validate="{ required: true, max_value: 1000000 }" placeholder="0" class="text-center"
                            @keydown="blockInvalidCharacters" />

                        <b-input-group-append>
                            <b-button id="add-limit-min" name="add-limit-min" variant="outline-secondary"
                                @click="incrementLimit('minStockLimit')">&nbsp; + &nbsp;</b-button>
                        </b-input-group-append>
                    </b-input-group>
                    <span v-show="errors.has('Min Stock')" class="help-block">
                        {{ errors.first('Min Stock') }}
                    </span>
                </b-col>
            </b-row>
        </b-form>
    </b-modal>
</template>

<script>
// Utils
import { AssetPoolDistributionUtil } from '@/utils/assetPoolDistributionUtil';
import { DateUtil } from '@/utils/dateutil';

// API
import assetPoolDistributionApi from '@/api/assetPoolDistributionApi';

// Others
import EventBus from '@/shared/event-bus';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import _ from 'lodash';

export default {
    name: 'update-asset-limit',
    components: {
        Loading,
    },
    props: {
        allCompaniesObj: {
            type: Object,
            required: true,
        },
        allConnectedCompaniesObj: {
            type: Object,
            required: true,
        },
        allAssetTypesObj: {
            type: Object,
            required: true,
        }
    },
    data() {
        return {
            distribution: {},
            currUserId: this.$store.getters.loggedUser.id,

            // Check for loader
            isLoading: false,
        };
    },
    computed: {
        disableConfirmButtons() {
            return this.isLoading;
        }
    },
    mounted() {
        EventBus.$on('onUpdateAssetLimit', (distribution) => {
            if (!_.isEmpty(distribution)) {
                this.distribution = { ...distribution };

                if (!this.distribution.assetLimit) {
                    this.distribution = { ...this.distribution, assetLimit: 0 };
                }

                if (!this.distribution.maxStockLimit) {
                    this.distribution = { ...this.distribution, maxStockLimit: 0 };
                }

                if (!this.distribution.minStockLimit) {
                    this.distribution = { ...this.distribution, minStockLimit: 0 };
                }

                if (!this.distribution.type || this.distribution.type.length === 0) {
                    let companyObj = this.allCompaniesObj[distribution.companyId];
                    let connectedCompanyObj = this.allConnectedCompaniesObj[distribution.connectedCompanyId];
                    let assetTypeObj = this.allAssetTypesObj[distribution.assetTypeId];
                    this.distribution.type = AssetPoolDistributionUtil.getDistributionType(distribution, companyObj, connectedCompanyObj, assetTypeObj.originId);
                }
            }
        });
    },
    methods: {
        blockInvalidCharacters(event) {
            // Check if the key pressed corresponds to one of the blocked characters
            if (event.key === '+' || event.key === '-' || event.key === 'e') {
                // Prevent the default behavior (typing the character)
                event.preventDefault();
            }
        },
        decrementLimit(fieldName) {
            if (this.distribution[fieldName] > 0) {
                this.distribution[fieldName] -= 1;
            }
        },
        incrementLimit(fieldName) {
            if (this.distribution[fieldName] < 1000000) {
                this.distribution[fieldName] += 1;
            }
        },

        async handleOk(evt) {
            // Prevent modal from closing
            evt.preventDefault();

            // show loading indicator
            this.isLoading = true;

            let isValid = await this.$validator.validateAll();
            if (!isValid) {
                this.$toaster.warning('Please address the field/s with invalid input');
                this.isLoading = false;
                return;
            }

            await this.handleSubmit();
        },
        updateFormValues(form) {
            let distribution = { ...form };

            distribution.addTotal = 0;
            distribution.notes = 'Asset Limit was updated by ' + this.currUserId;
            distribution.dateUpdated = DateUtil.getCurrentTimestamp();
            distribution.updatedBy = this.currUserId

            return AssetPoolDistributionUtil.cleanupFields(distribution);
        },
        getParam() {
            return {
                currUserId: this.currUserId,
                currTimeStamp: DateUtil.getCurrentTimestamp(),
                assetPoolDistribution: this.updateFormValues(this.distribution),
            };
        },
        async handleSubmit() {
            try {
                // show loading indicator
                this.isLoading = true;

                let { data } = await assetPoolDistributionApi.saveAssetPoolDistribution(
                    this.getParam()
                );

                if (data.isSuccess) {
                    this.$toaster.success('Updating Asset Limit was successful.');
                    EventBus.$emit('onCloseSaveAssetPoolDistribution', data.assetPoolDistribution);
                    this.$refs.modal.hide();
                } else {
                    this.$toaster.error(`Error updating asset limit "${this.distribution.connectedCompany}". Please try again.`);
                }

            } catch (error) {
                this.$toaster.error(`Error updating asset limit for "${this.distribution.connectedCompany}". Please try again.`);
            } finally {
                // hide loading indicator
                this.isLoading = false;
            }
        }
    },

    beforeDestroy() {
        EventBus.$off('onUpdateAssetLimit');
    },
};
</script>

<style scoped>
small {
    font-size: x-small;
}
</style>